import { useState } from 'react';
import { useRouter } from 'next/router';

import { Accordion, Alert, Box, Divider, Heading, List, Paragraph } from '@zillow/constellation-10';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import accessUtils from '../../../../utils/accessUtils';

import {
  wizardFlexContainerStyles,
  wizardFormSectionContainerStyles,
  wizardHeadingStyles,
  wizardParagraphStyles,
} from '../../../../styles/wizard';

import AmenitiesInteriorFormField from '../../../PostingPath/AmenitiesInteriorFormField';
import AmenitiesWithAdditionalAmenitiesFields from '../../../PostingPath/AmenitiesWithAdditionalAmenitiesFields';
import PropertyDescriptionFormField from '../../../PostingPath/PropertyDescriptionFormField';
import PageWizard from '../../../Wizard/PageWizard';

import type { WizardBodyTemplateProps } from '../../../WizardStepController';

const DescriptionAndAmenities = ({
  onGoToNextStep,
  onGoToPrevStep,
  onExitWizard,
  wizardTitle,
  progressStepper,
  totalSteps,
  currentStepIndex,
  showDesktopViewBreakpoint,
  ...rest
}: WizardBodyTemplateProps) => {
  const router = useRouter();
  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const [tipsExpanded, setTipsExpanded] = useState<Array<string>>([]);
  const handleTipsExpandedChange = (value: string | Array<string>) => {
    setTipsExpanded(typeof value === 'string' ? [value] : value);
  };

  const shouldShowBrokerFeeAlert = accessUtils.shouldShowBrokerFeeAlert({
    state: listingDetails?.state ?? '',
  });

  return (
    <PageWizard
      title=""
      wizardTitle={wizardTitle}
      progressStepper={progressStepper}
      totalSteps={totalSteps}
      currentStepIndex={currentStepIndex}
      onNextClick={onGoToNextStep || undefined}
      onBackClick={onGoToPrevStep || undefined}
      onExitTriggered={onExitWizard}
      showDesktopViewBreakpoint={showDesktopViewBreakpoint}
      {...rest}
    >
      <div data-testid="DescriptionAndAmenities">
        <Box css={wizardFlexContainerStyles}>
          <Box data-testid="FormSectionContainer" css={wizardFormSectionContainerStyles.raw()}>
            <Heading textStyle="heading-md" level={2} css={wizardHeadingStyles}>
              Description & amenities
            </Heading>
            <Paragraph css={wizardParagraphStyles}>
              Write several sentences describing upgrades, attractive amenities and other appealing details like the
              property’s proximity to transit, dining, shopping, and other local attractions. Then select all relevant
              included amenities.
            </Paragraph>
            <Heading level={2} textStyle="heading-xs" css={wizardHeadingStyles}>
              Property description
            </Heading>
            {shouldShowBrokerFeeAlert && (
              <Alert tone="info" css={{ marginBottom: '600' }}>
                <strong>MA Broker Fee Rule:</strong> Starting Aug 1, broker fees can be charged only to tenants who
                directly hire a broker
              </Alert>
            )}
            <PropertyDescriptionFormField showLabel={false} />
            <Accordion.Root
              expanded={tipsExpanded}
              onExpandedChange={handleTipsExpandedChange}
              css={{ marginTop: '800' }}
            >
              <Accordion.Item value="costs-tips">
                <Accordion.Header>
                  <Heading level={5}>Tips for property descriptions</Heading>
                </Accordion.Header>
                <Accordion.Panel>
                  <Box css={{ display: 'flex', flexDirection: 'column', gap: '600' }}>
                    <List.Root appearance="bullet">
                      <List.Item>
                        Market the property’s proximity to transit, dining, shopping, and other local attractions
                      </List.Item>
                      <List.Item>Mention upgrades, attractive amenities, and other appealing details</List.Item>
                      <List.Item>Indicate whether you live on-site</List.Item>
                    </List.Root>
                  </Box>
                </Accordion.Panel>
              </Accordion.Item>
            </Accordion.Root>

            <Divider css={{ marginBlock: '600' }} />
            <Heading textStyle="heading-sm" level={2} css={wizardHeadingStyles}>
              Interior amenities
            </Heading>
            <AmenitiesInteriorFormField />
            <Divider css={{ marginBlock: '600' }} />
            <Heading textStyle="heading-sm" level={2} css={wizardHeadingStyles}>
              Property amenities
            </Heading>
            <AmenitiesWithAdditionalAmenitiesFields />
          </Box>
        </Box>
      </div>
    </PageWizard>
  );
};

export default DescriptionAndAmenities;
